import { useEffect, useState } from 'react';

import {
  getMyVenue, createVenue, updateVenue,
  type VenueProfile as Profile, type VenueInput,
} from '../../lib/venue';
import { ApiError } from '../../lib/apiClient';
import { formatDate } from '../../lib/format';
import './VenuePages.css';

/**
 * Venue profile — create on first visit, edit thereafter.
 *
 * Creation is POST /venue/profile; editing is PATCH /venue/profile/:id. They are
 * different verbs and different paths, which is why this screen tracks whether a
 * profile exists rather than sending one shape to one endpoint.
 *
 * `approved` is displayed but not editable, and the engine does not accept it
 * here either. It is currently INERT — nothing in the engine sets or reads it —
 * so the UI says that plainly rather than implying a review is pending that
 * would never resolve.
 */

interface FormState {
  venueName: string;
  address: string;
  city: string;
  state: string;
  capacity: string;
  layoutOptions: string;
  technicalSpecs: string;
  pricingStructure: string;
  requirements: string;
  images: string;
}

const EMPTY: FormState = {
  venueName: '', address: '', city: '', state: '', capacity: '',
  layoutOptions: '', technicalSpecs: '', pricingStructure: '', requirements: '', images: '',
};

function toInput(f: FormState): VenueInput {
  const images = f.images.split('\n').map((s) => s.trim()).filter(Boolean);
  return {
    venueName: f.venueName.trim(),
    address: f.address.trim() || null,
    city: f.city.trim() || null,
    state: f.state.trim() || null,
    // The engine requires an integer or null, and rejects negatives.
    capacity: f.capacity.trim() === '' ? null : Number(f.capacity),
    layoutOptions: f.layoutOptions.trim() || null,
    technicalSpecs: f.technicalSpecs.trim() || null,
    pricingStructure: f.pricingStructure.trim() || null,
    requirements: f.requirements.trim() || null,
    images: images.length ? images : null,
  };
}

export function VenueProfilePage() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMyVenue()
      .then((p) => {
        if (cancelled) return;
        setProfile(p);
        if (p) {
          setForm({
            venueName: p.venueName ?? '',
            address: p.address ?? '',
            city: p.city ?? '',
            state: p.state ?? '',
            capacity: p.capacity?.toString() ?? '',
            layoutOptions: p.layoutOptions ?? '',
            technicalSpecs: p.technicalSpecs ?? '',
            pricingStructure: p.pricingStructure ?? '',
            requirements: p.requirements ?? '',
            images: (p.images ?? []).join('\n'),
          });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load your venue.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  async function save() {
    setBusy(true);
    setError(null);
    setFlash(null);
    try {
      const saved = profile
        ? await updateVenue(profile.venueId, toInput(form))
        : await createVenue(toInput(form));
      setProfile(saved);
      setFlash(profile ? 'Venue saved.' : 'Venue created. You can set your availability now.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save your venue.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="ven-status">Loading…</p>;

  const creating = !profile;
  const capacityInvalid = form.capacity.trim() !== ''
    && (!Number.isInteger(Number(form.capacity)) || Number(form.capacity) < 0);

  return (
    <div className="ven-page ven-page--narrow">
      <header className="ven-header">
        <div>
          <h1 className="ven-title">{creating ? 'Create your venue' : 'Venue profile'}</h1>
          <p className="ven-lede">
            What an organizer sees when they are deciding where to put a show.
          </p>
        </div>
      </header>

      {error && <p className="ven-error" role="alert">{error}</p>}
      {flash && <p className="ven-flash" role="status">{flash}</p>}

      <section className="ven-card">
        <div className="ven-form">
          <label className="field ven-form__wide">
            <span className="field__label">Venue name *</span>
            <input className="field__input" type="text" value={form.venueName} disabled={busy}
              onChange={(e) => set({ venueName: e.target.value })} />
          </label>

          <label className="field ven-form__wide">
            <span className="field__label">Address</span>
            <input className="field__input" type="text" value={form.address} disabled={busy}
              onChange={(e) => set({ address: e.target.value })} placeholder="935 E Colfax Ave" />
          </label>

          <label className="field">
            <span className="field__label">City</span>
            <input className="field__input" type="text" value={form.city} disabled={busy}
              onChange={(e) => set({ city: e.target.value })} placeholder="Denver" />
          </label>

          <label className="field">
            <span className="field__label">State</span>
            <input className="field__input" type="text" value={form.state} disabled={busy}
              onChange={(e) => set({ state: e.target.value })} placeholder="CO" />
          </label>

          <label className="field">
            <span className="field__label">Capacity</span>
            <input className="field__input mono" type="number" min="0" step="1"
              value={form.capacity} disabled={busy}
              onChange={(e) => set({ capacity: e.target.value })} />
          </label>
        </div>
        {capacityInvalid && (
          <p className="ven-error" role="alert">
            Capacity must be a whole number of people, or left blank.
          </p>
        )}
      </section>

      <section className="ven-card">
        <h2 className="ven-section__title">The room</h2>
        <p className="ven-section__note">
          Free text, an organizer reads these to work out whether their show fits.
        </p>
        <div className="ven-form">
          <label className="field ven-form__wide">
            <span className="field__label">Layout options</span>
            <textarea className="field__input" rows={3} value={form.layoutOptions} disabled={busy}
              onChange={(e) => set({ layoutOptions: e.target.value })}
              placeholder="GA floor 1,200 · balcony seated 400 · cabaret 300" />
          </label>
          <label className="field ven-form__wide">
            <span className="field__label">Technical specs</span>
            <textarea className="field__input" rows={4} value={form.technicalSpecs} disabled={busy}
              onChange={(e) => set({ technicalSpecs: e.target.value })}
              placeholder="PA, console, lighting rig, power, load-in access." />
          </label>
          <label className="field ven-form__wide">
            <span className="field__label">Pricing structure</span>
            <textarea className="field__input" rows={3} value={form.pricingStructure} disabled={busy}
              onChange={(e) => set({ pricingStructure: e.target.value })}
              placeholder="$4,000 flat against 10% of bar, or 80/20 door split." />
          </label>
          <label className="field ven-form__wide">
            <span className="field__label">Requirements</span>
            <textarea className="field__input" rows={3} value={form.requirements} disabled={busy}
              onChange={(e) => set({ requirements: e.target.value })}
              placeholder="Insurance certificate, load-in before 17:00, curfew 23:30." />
          </label>
          <label className="field ven-form__wide">
            <span className="field__label">Image URLs, one per line</span>
            <textarea className="field__input mono" rows={3} value={form.images} disabled={busy}
              onChange={(e) => set({ images: e.target.value })}
              placeholder="https://…/room.jpg" />
          </label>
        </div>
      </section>

      {profile && (
        <>
          <dl className="ven-facts">
            <div><dt>Venue id</dt><dd className="mono ven-hint">{profile.venueId}</dd></div>
            <div><dt>Created</dt><dd>{formatDate(profile.createdAt)}</dd></div>
          </dl>
          <p className="ven-hint">
            Your profile carries an <span className="mono">approved</span> flag, currently{' '}
            <strong>{String(profile.approved)}</strong>. Nothing in Voleto reads or sets it
            today, so it does not gate anything, organizers can book you either way. It is
            shown here rather than hidden so the state is not mistaken for a review in
            progress.
          </p>
        </>
      )}

      <div className="ven-actions">
        <button type="button" className="button button--primary"
          disabled={busy || !form.venueName.trim() || capacityInvalid} onClick={save}>
          {busy ? 'Saving…' : creating ? 'Create venue' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
